import { HoverLink } from '@campfire/hover-link';
import { Typography, Box, Grid, IconButton, Button, Card, CardContent, Theme } from '@material-ui/core';
import { Event, Add, MoreVertOutlined, PersonAdd, Delete } from '@material-ui/icons';
import { TabletButton } from '@campfire/tablet-button';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { memo, useState } from 'react';
import { TooltipMenu } from '../../../../common/tooltip-menu';
import { useApiUrl } from '../../../../global/config/useApiUrl';
import { useSnackbar } from '../../../../global/config/useSnackbar';
import { useCampfireFetch } from '../../../../global/network/useCampfireFetch';
import { useCampfireQuery } from '../../../../global/network/useCampfireQuery';
import { ActivityFilters } from '../../../../global/activity-filters/ActivityFilters';
import { ACTIVITIES_FILTERS_LIST } from '../../../../global/activity-filters/activity-filters-activity.gql';
import {
  GetAllActivitiesForVolunteer,
  GetAllActivitiesForVolunteerVariables,
} from '../../../../global/activity-filters/__generated__/GetAllActivitiesForVolunteer';
import { useUser } from '../../../../global/auth/useUser';
import {
  useEnrolWaitlistingFetch,
  useUnenrolWaitlist,
} from '../../activity-management/activity-form-v2/activity-form-actions-v2';
import { VolunteerCommonProfileActivityWaitlisting } from '../__generated__/VolunteerCommonProfileActivityWaitlisting';
import { useVolunteerCommonProfileContext } from '../VolunteerCommonProfileContext';

export const VolunteerActivityWaitlistingsSection = memo(
  ({ activityWaitlistings }: { activityWaitlistings: VolunteerCommonProfileActivityWaitlisting[] }) => {
    const {
      volunteerNameOptions: { preferredName },
      volunteer,
      reloadNewData,
    } = useVolunteerCommonProfileContext();
    const [dialogOpen, setDialogOpen] = useState<boolean>(false);
    const apiUrl = useApiUrl();
    const { setSnackbar } = useSnackbar();
    const { run } = useCampfireFetch({ defer: true });
    const { getManagerIdentity, user } = useUser();
    const { programManagerIdentities } = getManagerIdentity();
    const managedProgramIds = programManagerIdentities.flatMap((p) => p.programId);

    const programIds = volunteer?.programs.map((a) => a.programId) ?? [];
    const filteredProgramIds = user.userIdentityService.isProgramManager
      ? programIds.filter((p) => managedProgramIds.includes(p))
      : programIds;

    const { data } = useCampfireQuery<GetAllActivitiesForVolunteer, GetAllActivitiesForVolunteerVariables>(
      ACTIVITIES_FILTERS_LIST,
      {
        options: {
          variables: {
            programIds: filteredProgramIds,
          },
        },
      }
    );

    const setSelectedActivities = async (activityList: string[]) => {
      if (!volunteer?.volunteerId) return;

      try {
        const response = await run({
          method: 'post',
          data: {
            items: [
              {
                volunteerId: volunteer.volunteerId,
                activityIds: activityList,
              },
            ],
          },
          url: `${apiUrl}/vm/activity/waitlist/manager-add`,
        });

        if (response.ok) {
          setSnackbar({
            open: true,
            message: `Successfully added to ${activityList.length} waitlist${activityList.length > 1 ? 's' : ''}`,
            variant: 'success',
          });
        } else {
          setSnackbar({
            open: true,
            message: 'Some waitlist additions could not be created',
            variant: 'error',
          });
        }
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Failed to add to waitlists',
          variant: 'error',
        });
      } finally {
        reloadNewData();
      }
    };

    const allActivities = data?.vm.programs.flatMap((a) => a.activities);
    const waitlistedActivities = activityWaitlistings.flatMap((a) => a.activity);

    const activitiesList =
      waitlistedActivities.length === 0
        ? allActivities
        : allActivities?.filter((a) => !waitlistedActivities.some((b) => b.activityId === a.activityId));
    return (
      <Grid container spacing={2} direction='column' style={{ position: 'relative' }}>
        <Grid item xs={12} style={{ textAlign: 'left' }}>
          <Box display={'flex'} justifyContent={'space-between'}>
            <Box>
              <Typography
                style={{ fontWeight: 600, fontSize: '18px', display: 'flex', alignItems: 'center', color: '#333' }}
              >
                <Event style={{ fontSize: '14px', paddingRight: '5px' }} /> Activity Waitlistings
              </Typography>
              <Typography variant='body2' style={{ color: '#464646' }} align='left'>
                Activities that {preferredName} is waitlisted for
              </Typography>
            </Box>
            <Box>
              <TabletButton
                size='small'
                variant='contained'
                color='primary'
                aria-controls='options-menu'
                aria-haspopup='true'
                onClick={() => setDialogOpen(true)}
                startIcon={<Add />}
                style={{
                  height: 36,
                  border: '1px solid transparent',
                  boxSizing: 'border-box',
                  borderRadius: '4px',
                  padding: '6px 12px',
                  marginTop: '4px',
                  color: '#fff',
                }}
              >
                Add
              </TabletButton>
            </Box>
          </Box>
          <hr style={{ borderTop: '.5px solid #e7e7e7', margin: '20px 0' }} />
          {activityWaitlistings
            .filter((activityWaitlisting) => activityWaitlisting.activityWaitlistingId !== null)
            .map((activityWaitlisting) => (
              <WaitlistingCard
                key={activityWaitlisting.activityWaitlistingId}
                waitlisting={activityWaitlisting}
                preferredName={preferredName}
                reloadData={reloadNewData}
              />
            ))}
          {activityWaitlistings.length === 0 && (
            <Typography
              variant='body2'
              style={{ fontSize: '13px', color: '#494949', textAlign: 'center', padding: '2rem 0' }}
            >
              No results found
            </Typography>
          )}
        </Grid>
        {dialogOpen && activitiesList && (
          <ActivityFilters
            activities={activitiesList}
            selectedActivities={[]}
            setSelectedActivities={setSelectedActivities}
            open={dialogOpen}
            onClose={() => setDialogOpen(false)}
            onCancel={() => setDialogOpen(false)}
            fullScreen={false}
            simple
            addVol
          />
        )}
      </Grid>
    );
  }
);

const useCardStyles = makeStyles((theme: Theme) =>
  createStyles({
    card: {
      marginBottom: '12px',
      borderRadius: '8px',
      border: '1px solid #e0e0e0',
      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
      transition: 'box-shadow 0.2s ease-in-out',
      '&:hover': {
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
      },
    },
    cardContent: {
      padding: '16px !important',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
    },
    activityInfo: {
      flex: 1,
    },
    activityName: {
      fontSize: '14px',
      fontWeight: 600,
      color: '#333',
      marginBottom: '4px',
    },
    programName: {
      fontSize: '12px',
      color: '#666',
    },
    menuItem: {
      color: theme.alert.blue.extraLight,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-start',
      fontSize: '14px',
      fontWeight: 500,
      textTransform: 'none',
      width: '100%',
      padding: '6px 8px',
      '& span': { fontSize: '14px' },
      '& svg': { fontSize: '16px' },
    },
    menuItemDestructive: {
      color: theme.alert.red.light,
    },
  })
);

interface WaitlistingCardProps {
  waitlisting: VolunteerCommonProfileActivityWaitlisting;
  preferredName: string;
  reloadData: () => void;
}

const WaitlistingCard: React.FC<WaitlistingCardProps> = memo(({ waitlisting, preferredName, reloadData }) => {
  const classes = useCardStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const { setSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);

  const enrolWaitlistingFetch = useEnrolWaitlistingFetch();
  const unenrolWaitlistFetch = useUnenrolWaitlist();

  const handleAction = async (action: string) => {
    setMenuOpen(false);
    setLoading(true);

    try {
      switch (action) {
        case 'enrol':
          await enrolWaitlistingFetch.run({ activityWaitlistingId: waitlisting.activityWaitlistingId });
          setSnackbar({
            open: true,
            message: `Successfully added ${preferredName} to ${waitlisting.activity.name}`,
            variant: 'success',
          });
          break;
        case 'remove':
          await unenrolWaitlistFetch.run({ activityWaitlistingId: waitlisting.activityWaitlistingId });
          setSnackbar({
            open: true,
            message: `Successfully removed ${preferredName} from ${waitlisting.activity.name} waitlist`,
            variant: 'success',
          });
          break;
        default:
          console.warn(`Unknown action: ${action}`);
          break;
      }
      reloadData();
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Action failed. Please try again.',
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card className={classes.card}>
      <CardContent className={classes.cardContent}>
        <HoverLink
          to={`/management/activity-management?activityId=${waitlisting.activity.activityId}&programId=${waitlisting.activity.program.programId}`}
          hoverColor='primary'
          disableUnderline
          className={classes.activityInfo}
        >
          <Typography className={classes.activityName}>{waitlisting.activity.name}</Typography>
          <Typography className={classes.programName}>{waitlisting.activity.program.name}</Typography>
        </HoverLink>

        <TooltipMenu
          open={menuOpen}
          onClose={() => setMenuOpen(false)}
          title={
            <Box display='flex' flexDirection='column' alignItems='flex-start' style={{ padding: '5px 10px' }}>
              <Button className={classes.menuItem} startIcon={<PersonAdd />} onClick={() => handleAction('enrol')}>
                Add to Team
              </Button>
              <Button
                className={`${classes.menuItem} ${classes.menuItemDestructive}`}
                startIcon={<Delete />}
                onClick={() => handleAction('remove')}
              >
                Remove from Waitlist
              </Button>
            </Box>
          }
        >
          <IconButton
            onClick={() => setMenuOpen(!menuOpen)}
            style={{ color: '#0000008A', padding: '0.5rem' }}
            disabled={loading}
          >
            <MoreVertOutlined />
          </IconButton>
        </TooltipMenu>
      </CardContent>
    </Card>
  );
});
